import {
  API_CALL_REQUEST,
  API_CALL_FAILURE,
  API_CALL_SUCCESS,
  DISMISS_NOTIFICATION_BANNER,
} from 'actions/actionConstants'
import {
  FETCH_LANDING_PAGE,
  FETCH_MAGAZINE_ARTICLES,
  FETCH_NOTIFICATION_BANNER,
  FETCH_BANNERS,
  FETCH_FAQ,
  FETCH_LIST_BANNERS,
  FETCH_DREAM_SITE_BLOG_POSTS,
  FETCH_SITE_BANNER,
  CLEAR_SITE_BANNER,
  FETCH_DREAM_SITE_BLOG_POST,
  FETCH_BANNER_GROUP,
  FETCH_TARGETED_BANNER_CAMPAIGNS,
} from 'actions/apiActionConstants'
import { reducerSwitch, createReducer } from 'lib/redux/reducerUtils'
import { ApiAction } from 'middlewares/apiMiddleware'

export const initialContentState: App.ContentState = {
  dreamSiteBlogPosts: {},
  dreamSiteBlogPost: {},
  notifications: {},
  siteBanner: {
    data: undefined,
  },
  banners: {},
  listBanners: {},
  bannerGroup: {},
  landingPages: {},
  landingPageErrors: {},
  magazineArticles: {
    posts: [],
    error: undefined,
    fetching: false,
  },
  faqs: {
    legal: {},
    destinationProduct: {},
  },
  targetedBannerCampaigns: [],
}

const apiRequests = reducerSwitch<App.ContentState>({
  [FETCH_DREAM_SITE_BLOG_POSTS]: (state, action) => ({
    dreamSiteBlogPosts: {
      ...state.dreamSiteBlogPosts,
      [action.key]: [],
    },
  }),
  [FETCH_DREAM_SITE_BLOG_POST]: (state, action) => ({
    dreamSiteBlogPost: {
      ...state.dreamSiteBlogPost,
      [action.key]: {
        fetching: true as const,
        error: undefined,
      },
    },
  }),
  [FETCH_LANDING_PAGE]: (state, action) => ({
    landingPageErrors: {
      ...state.landingPageErrors,
      [action.key]: undefined,
    },
  }),
  [FETCH_MAGAZINE_ARTICLES]: (state) => ({
    magazineArticles: {
      ...state.magazineArticles,
      fetching: true,
    },
  }),
  [FETCH_FAQ]: (state, action: { type:string, slug: string, faqType: 'destinationProduct' | 'legal' }) => ({
    faqs: {
      ...state.faqs,
      [action.faqType]: {
        ...state.faqs[action.faqType],
        [action.slug]: {
          fetching: true,
        },
      },
    },
  }),
  [FETCH_SITE_BANNER]: (state) => ({
    siteBanner: {
      fetching: true,
      data: state.siteBanner.data,
    },
  }),
  [FETCH_TARGETED_BANNER_CAMPAIGNS]: (_state) => ({
    targetedBannerCampaigns: [],
  }),
})

const apiSuccesses = reducerSwitch<App.ContentState>({
  [FETCH_NOTIFICATION_BANNER]: (state, action) => ({
    notifications: {
      ...state.notifications,
      [action.slug]: action.data,
    },
  }),
  [FETCH_BANNERS]: (state, action) => ({
    banners: {
      ...state.banners,
      [action.location]: action.data,
    },
  }),
  [FETCH_LIST_BANNERS]: (state, action) => ({
    listBanners: {
      ...state.listBanners,
      [action.location]: action.data,
    },
  }),
  [FETCH_BANNER_GROUP]: (state, action) => ({
    bannerGroup: {
      ...state.bannerGroup,
      [action.slug]: action.data,
    },
  }),
  [FETCH_LANDING_PAGE]: (state, action) => ({
    landingPages: {
      ...state.landingPages,
      [action.key]: action.data,
    },
  }),
  [FETCH_MAGAZINE_ARTICLES]: (state, action) => ({
    magazineArticles: {
      ...state.magazineArticles,
      posts: action.data,
      fetching: false,
    },
  }),
  [FETCH_FAQ]: (state, action: { type:string, slug: string, faqType: 'destinationProduct' | 'legal', data: any }) => ({
    faqs: {
      ...state.faqs,
      [action.faqType]: {
        ...state.faqs[action.faqType],
        [action.slug]: {
          content: action.data,
          fetching: false,
        },
      },
    },
  }),
  [FETCH_DREAM_SITE_BLOG_POSTS]: (state, action) => ({
    dreamSiteBlogPosts: {
      ...state.dreamSiteBlogPosts,
      [action.key]: action.data,
    },
  }),
  [FETCH_DREAM_SITE_BLOG_POST]: (state, action) => ({
    dreamSiteBlogPost: {
      ...state.dreamSiteBlogPost,
      [action.key]: {
        data: action.data,
        fetching: false as const,
        error: undefined,
      },
    },
  }),
  [FETCH_SITE_BANNER]: (state, action) => ({
    siteBanner: {
      fetching: false,
      data: action.data,
    },
  }),
  [FETCH_TARGETED_BANNER_CAMPAIGNS]: (_state, action: ApiAction<Array<App.TargetedBannerCampaign>>) => ({
    targetedBannerCampaigns: action.data ?? [],
  }),
})

const apiFailures = reducerSwitch<App.ContentState>({
  [FETCH_LANDING_PAGE]: (state, action) => ({
    landingPageErrors: {
      ...state.landingPageErrors,
      [action.key]: action.error,
    },
  }),
  [FETCH_MAGAZINE_ARTICLES]: (state, action) => ({
    magazineArticles: {
      ...state.magazineArticles,
      fetching: false,
      error: action.error,
    },
  }),
  [FETCH_DREAM_SITE_BLOG_POST]: (state, action) => ({
    dreamSiteBlogPost: {
      ...state.dreamSiteBlogPost,
      [action.key]: {
        fetching: false,
        error: action.error,
      },
    },
  }),
  [FETCH_SITE_BANNER]: (state, action) => ({
    siteBanner: {
      fetching: false,
      data: state.siteBanner.data,
      error: action.error,
    },
  }),
  [FETCH_TARGETED_BANNER_CAMPAIGNS]: (_state) => ({
    targetedBannerCampaigns: [],
  }),
})

export default createReducer<App.ContentState>(initialContentState, {
  [API_CALL_REQUEST]: (state, action) => apiRequests(action.api)(state, action),
  [API_CALL_FAILURE]: (state, action) => apiFailures(action.api)(state, action),
  [API_CALL_SUCCESS]: (state, action) => apiSuccesses(action.api)(state, action),
  [DISMISS_NOTIFICATION_BANNER]: (state, action) => ({
    notifications: {
      ...state.notifications,
      [action.slug]: {
        ...state.notifications[action.slug],
        show: false,
      },
    },
  }),
  [CLEAR_SITE_BANNER]: () => ({
    siteBanner: {
      data: undefined,
    },
  }),
})
